<template>
  <div class="d-flex align-center">
    <AdminInvoicesFiltersValues :customer="customer" :host="host" ref="filtersValues" />

    <v-menu
      v-model="menu"
      :close-on-content-click="false"
      transition="scale-transition"
      origin="top right"
      offset-y
      bottom
      left
    >
      <template v-slot:activator="{ on, attrs }">
        <v-btn v-bind="attrs" v-on="on" color="primary" class="mr-n3" outlined small>
          <span class="caption">
            {{ $t('filters.title') }}
          </span>
          <v-icon right small>mdi-filter-outline</v-icon>
        </v-btn>
      </template>

      <v-list class="mw-350 py-3">
        <v-list-item class="mb-2">
          <FilterDate
            :fetchData="fetchAdminInvoices"
            startDateProp="creationStartDate"
            endDateProp="creationEndDate"
            ref="filterDate"
          />
        </v-list-item>

        <v-list-item class="mb-2">
          <v-autocomplete
            v-model="realEstateId"
            @change="(customer) => applyFilterCustomer(customer)"
            :search-input.sync="realEstateSearch"
            :loading="realEstateLoading"
            :items="realEstateList"
            :no-data-text="$t('emptyState.noDataDoTheSearch')"
            :label="$t('filters.customers')"
            prepend-inner-icon="mdi-magnify"
            item-value="id"
            item-text="name"
            return-object
            hide-details
            clearable
            outlined
            dense
          />
        </v-list-item>

        <v-list-item class="mb-2">
          <v-autocomplete
            v-model="hostId"
            @change="(host) => applyFilterHost(host)"
            :search-input.sync="hostSearch"
            :loading="hostLoading"
            :items="hostSearchResults"
            :no-data-text="$t('emptyState.noDataDoTheSearch')"
            :label="$t('filters.hosts')"
            prepend-inner-icon="mdi-magnify"
            item-value="id"
            item-text="name"
            return-object
            hide-details
            clearable
            outlined
            dense
          />
        </v-list-item>

        <v-list-item class="mb-2">
          <v-text-field
            v-model="cnpj"
            @change="applyFilterCnpj"
            :label="$t('movement.filters.document')"
            prepend-inner-icon="mdi-card-account-details-outline"
            clearable
            hide-details
            outlined
            dense
          />
        </v-list-item>

        <v-list-item class="mb-2">
          <v-autocomplete
            v-model="receiver"
            @change="(receiver) => applyFilter({ receiver })"
            :items="receivers"
            :label="$t('filters.receiverType')"
            item-value="value"
            item-text="text"
            hide-details
            clearable
            outlined
            dense
          />
        </v-list-item>

        <v-list-item class="mb-2">
          <v-select
            v-model="hasTransfer"
            @change="(hasTransfer) => applyFilter({ hasTransfer })"
            :items="hasTransferOptions"
            :label="$t('filters.hasTransfer')"
            hide-details
            clearable
            outlined
            dense
          />
        </v-list-item>

        <v-list-item>
          <v-select
            v-model="subscriptionType"
            @change="(subscriptionType) => applyFilter({ subscriptionType })"
            :items="subscriptionTypeOptions"
            :label="$t('filters.subscriptionType')"
            item-value="value"
            item-text="text"
            hide-details
            clearable
            outlined
            dense
            multiple
            small-chips
          />
        </v-list-item>
      </v-list>
    </v-menu>
  </div>
</template>

<script>
import { mapActions, mapMutations, mapGetters } from 'vuex'
import AdminInvoicesFiltersValues from './AdminInvoicesFiltersValues'
import FilterDate from '@/components/filters/FilterDate'
import store from '@/store'

export default {
  name: 'AdminInvoicesFilters',

  components: {
    AdminInvoicesFiltersValues,
    FilterDate,
  },

  computed: {
    ...mapGetters(['branches', 'realEstateList', 'hostSearchResults']),
  },

  watch: {
    hostSearch(value) {
      if (!value && value?.length <= 2) return
      this.hostLoading = true
      clearTimeout(this.timer)
      this.timer = setTimeout(() => {
        this.fetchHostsBySearchText(value).finally(() => (this.hostLoading = false))
      }, 1000)
    },

    realEstateSearch(value) {
      if (!value && value?.length <= 2) return
      this.realEstateLoading = true
      clearTimeout(this.timer)
      this.timer = setTimeout(() => {
        this.fetchRealEstatesBySearchText(value).finally(() => (this.realEstateLoading = false))
      }, 1000)
    },
  },

  methods: {
    ...mapMutations(['setFilters']),
    ...mapActions([
      'fetchAdminInvoices',
      'fetchHostsBySearchText',
      'fetchRealEstatesBySearchText',
    ]),

    resetValues() {
      this.receiver = null
      this.customer = null
      this.realEstateId = null
      this.host = null
      this.hostId = null
      this.cnpj = null
      this.subscriptionType = null
    },

    applyFilter(filter) {
      this.setFilters({ ...store.getters.filters, ...filter })
      this.fetchAdminInvoices()
    },

    applyFilterHost(host) {
      if (!host) {
        this.host = null
        this.hostId = null
        this.cnpj = null
        this.setFilters({ ...store.getters.filters, hostId: null, cnpj: null })
        this.$nextTick(() => this.fetchAdminInvoices())
        return
      }

      this.host = host
      this.hostId = host.id
      this.cnpj = host.document

      this.setFilters({
        ...store.getters.filters,
        hostId: host.id,
        cnpj: host.document,
      })

      this.$nextTick(() => this.fetchAdminInvoices())
    },

    applyFilterCustomer(customer) {
      if (!customer) {
        this.setFilters({ ...store.getters.filters, realEstateId: null })
        this.$nextTick(() => this.fetchAdminInvoices())
        return
      }

      this.customer = customer
      this.setFilters({ ...store.getters.filters, realEstateId: customer.id })
      this.$nextTick(() => this.fetchAdminInvoices())
    },

    applyFilterCnpj() {
      this.host = null
      this.hostId = null

      this.setFilters({
        ...store.getters.filters,
        cnpj: this.cnpj,
        hostId: null,
      })

      this.fetchAdminInvoices()
    },
  },

  data() {
    return {
      menu: false,
      timer: null,

      dates: null,

      host: null,
      hostId: null,
      cnpj: null,
      hostSearch: null,
      hostLoading: false,

      customer: null,
      realEstateId: null,
      realEstateSearch: null,
      realEstateLoading: false,

      branchId: null,
      branchSearch: null,
      branchLoading: false,

      receiver: null,
      receivers: [
        { text: this.$t('hausefulInvoices.HAUSEFUL'), value: 'HAUSEFUL' },
        { text: this.$t('hausefulInvoices.CUSTOMER'), value: 'CUSTOMER' },
      ],

      hasTransfer: {
        text: this.$t('filters.no'),
        value: false,
      },
      hasTransferOptions: [
        { text: this.$t('filters.yes'), value: true },
        { text: this.$t('filters.no'), value: false },
      ],

      subscriptionType: null,
      subscriptionTypeOptions: [
        { text: this.$t('realEstate.subscriptionTypes.TRIAL'), value: 'TRIAL' },
        { text: this.$t('realEstate.subscriptionTypes.SINGLE_PURCHASE'), value: 'SINGLE_PURCHASE' },
        { text: this.$t('realEstate.subscriptionTypes.PREPAID'), value: 'PREPAID' },
        { text: this.$t('realEstate.subscriptionTypes.PREPAID_SAAS'), value: 'PREPAID_SAAS' },
        { text: this.$t('realEstate.subscriptionTypes.SUBSCRIPTION_TRIMESTER'), value: 'SUBSCRIPTION_TRIMESTER' },
        { text: this.$t('realEstate.subscriptionTypes.SUBSCRIPTION_SEMESTER'), value: 'SUBSCRIPTION_SEMESTER' },
        { text: this.$t('realEstate.subscriptionTypes.SUBSCRIPTION_NONAMESTER'), value: 'SUBSCRIPTION_NONAMESTER' },
        { text: this.$t('realEstate.subscriptionTypes.SUBSCRIPTION_YEAR'), value: 'SUBSCRIPTION_YEAR' },
        { text: this.$t('realEstate.subscriptionTypes.SUBSCRIPTION_LIFELONG'), value: 'SUBSCRIPTION_LIFELONG' },
        { text: this.$t('realEstate.subscriptionTypes.POSTPAID'), value: 'POSTPAID' },
        { text: this.$t('realEstate.subscriptionTypes.POSTPAID_SAAS'), value: 'POSTPAID_SAAS' },
      ],
    }
  },
}
</script>
